import type { ReactNode } from 'react';

/**
 * The focus ring on the chrome: the top bar, the sidebar and the menus that open from them. These look
 * the same in light and dark, so the ring is a fixed light one with no offset. The content ring
 * (`.focus-ring`) is themed and would put a card-coloured halo on the dark bar.
 */
export const chromeFocusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-0';

/**
 * A dropdown that opens from the top bar or the sidebar (New Content, the account menu, the workspace
 * list, the apps menu): the dark chrome panel in BOTH themes, like the apps panel of the spec.
 *
 * Below `lg`, where it opens over a dimmed page from the header, the rail or the bottom bar, it is
 * rounder, holds its rows off its edges, and scrolls inside itself if the screen is too short for it.
 */
export const chromeMenuPanel =
  'rounded-xl border border-chrome-control-line bg-chrome-control shadow-[0_16px_40px_rgb(0_0_0/.35)] [color-scheme:dark] max-lg:motion-safe:animate-menu-in max-lg:max-h-[calc(100dvh-5.5rem)] max-lg:overflow-y-auto max-lg:rounded-2xl max-lg:p-1.5';

/**
 * A chrome menu row below `lg`: at least 52px tall (44px at the 80% tablet scale, whichever is more)
 * with a 22px glyph, sized for a finger. Shared by every row of the chrome menus.
 */
export const chromeMenuRowTouch =
  'max-lg:min-h-[max(var(--touch-target),3.25rem)] max-lg:rounded-lg max-lg:[&_svg]:h-[1.375rem] max-lg:[&_svg]:w-[1.375rem]';

/**
 * One entry of a chrome menu: its glyph, then its label. Shared by the New Content menu and the account
 * menu so the two keep the same markup. The row spans the panel, which clips it, so its ring is inset.
 */
export function MenuItem({
  icon,
  children,
  onClick,
  disabled,
}: {
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-chrome-text transition-colors hover:bg-chrome-hover max-lg:text-base ${chromeMenuRowTouch} ${chromeFocusRing} focus-visible:ring-inset disabled:cursor-not-allowed disabled:text-chrome-muted`}
    >
      <span className="text-chrome-muted">{icon}</span>
      {children}
    </button>
  );
}

/**
 * The layer that dims the page behind a menu open below `lg` (the desktop menus never dimmed it). It
 * starts under the top bar, which stays lit. A press on it closes the menu: it is rendered inside the
 * menu's own box (to share its stacking context), where the menu's outside-press handling
 * (useDismissable) does not see it, so it closes the menu itself. Its top is the top bar's height: 4rem
 * on a phone, the bar's exact 64px on a tablet (5rem at the 12.8px root, Topbar).
 */
export function MenuScrim({ onDismiss, className = '' }: { onDismiss: () => void; className?: string }) {
  return (
    <div
      aria-hidden
      onMouseDown={onDismiss}
      className={`fixed inset-x-0 bottom-0 top-16 bg-black/40 md:max-lg:top-[5rem] motion-safe:animate-fade-in lg:hidden ${className}`}
    />
  );
}
